import slug from 'slug';
import React from 'react';

import { basesUrl } from '@/lib/BasesUrl';
import { classNames } from '@/shared/helpers';
import { IPageContext } from '@/interfaces/pageContext';
import securePaymentIcon from '@/static/images/secure_payment.svg';
import { PaymentMethodsComponent } from '@/components/PaymentMethods';
import { socialMediaIcons } from '@/components/Footers/MainFooter/utils';
import { InternalOrExternalPage } from '@/components/Headers/MainHeader/Atoms/InternalOrExternalPage';

import './styles.scss';
import ViewMore from '@/components/ViewMore';

type Props = {
  pageContext: IPageContext;
};

const AlternativeFooter = ({ pageContext }: Props) => {
  const { pathLang, appData, lang, categoriesWithProducts } = pageContext;
  const { locale, templateData, products } = appData;
  const { general, footer } = templateData;
  const pages = [...categoriesWithProducts, ...templateData.pages];
  const {
    logoUrl,
    facebookUrl,
    instagramUrl,
    tripAdvisorUrl,
    tikTokUrl,
    youtubeUrl,
    twitterUrl,
  } = footer;

  const { longName } = general;
  const currentYear = new Date().getFullYear();
  let logoSize = parseInt(footer.logoSize);
  const footerLocale = locale.footer;
  const genericsLocale = locale.generics;
  const icons = socialMediaIcons('alternative');
  const shouldApplyContrast = general.colors.contrast;
  const prefixPath = pathLang === '' ? pathLang : `/${lang}`;

  return (
    <article
      className={classNames(
        'container',
        shouldApplyContrast ? 'text-with-contrast' : 'text-without-contrast'
      )}
    >
      <section className="logo-and-midias" aria-label="logo-and-midias">
        <a href={`/${pathLang}`}>
          <img
            src={logoUrl}
            height={logoSize ? logoSize : '60'}
            alt={footerLocale.brand_logo_icon}
          />
        </a>

        <div
          className={classNames(
            'social-midia',
            shouldApplyContrast && 'image-with-contrast'
          )}
        >
          {instagramUrl ? (
            <a
              target="_blank"
              rel="noreferrer"
              href={instagramUrl}
              className="social-midia-icon"
            >
              <img src={icons.instagramIcon} alt="instagram icon" />
            </a>
          ) : null}

          {facebookUrl ? (
            <a
              target="_blank"
              rel="noreferrer"
              href={facebookUrl}
              className="social-midia-icon"
            >
              <img src={icons.facebookIcon} alt="facebook icon" />
            </a>
          ) : null}

          {tikTokUrl ? (
            <a
              target="_blank"
              rel="noreferrer"
              href={tikTokUrl}
              className="social-midia-icon"
            >
              <img src={icons.tiktokIcon} alt="tiktok icon" />
            </a>
          ) : null}

          {youtubeUrl ? (
            <a
              target="_blank"
              rel="noreferrer"
              href={youtubeUrl}
              className="social-midia-icon"
            >
              <img src={icons.youtubeIcon} alt="youtube icon" />
            </a>
          ) : null}

          {tripAdvisorUrl ? (
            <a
              target="_blank"
              rel="noreferrer"
              href={tripAdvisorUrl}
              className="social-midia-icon"
            >
              <img src={icons.tripAdvisorIcon} alt="tripadvisor icon" />
            </a>
          ) : null}

          {twitterUrl ? (
            <a
              target="_blank"
              rel="noreferrer"
              href={twitterUrl}
              className="social-midia-icon"
            >
              <img src={icons.twitterIcon} alt="twitter icon" />
            </a>
          ) : null}
        </div>
      </section>

      <div className="payment-container">
        <div className="payment-methods">
          <PaymentMethodsComponent
            paymentMethods={pageContext.appData.paymentMethods}
          />
        </div>

        <div className="typ-3 payment-methods-info">
          <img
            src={securePaymentIcon}
            alt={footerLocale.secure_payment_ensurance_icon}
          />
          <p>{genericsLocale.secure_payment_ensurance}</p>
        </div>
      </div>

      <div
        className={classNames(
          'divisor-border',
          shouldApplyContrast
            ? 'border-with-contrast'
            : 'border-without-contrast'
        )}
      />

      <div className="products-pages-and-terms">
        <div className="pages">
          <div className="page">
            <a href={`/${pathLang}${basesUrl.products}/`}>
              <p className="typ-1">{footerLocale.products.toUpperCase()}</p>
            </a>
            <ViewMore
              viewMoreText={footerLocale.view_more_products}
              viewLessText={footerLocale.view_less_products}
              dataLength={products.length}
            >
              <ul className="typ-4">
                {products.map(({ id, name, currentPath }) => (
                  <li key={id}>
                    <a href={`${prefixPath}${currentPath}`}>{name}</a>
                  </li>
                ))}
              </ul>
            </ViewMore>
          </div>

          {pages.map((page, index) => (
            <div className="page" key={index}>
              {page.isCategory ? (
                <>
                  {page.products.length > 0 ? (
                    <>
                      <a
                        href={`/${pathLang}${
                          basesUrl.productsByCategory
                        }/${slug(page.categoryNamePT)}`}
                        className="main-content typ-1"
                      >
                        <p>{(page.menu || page.name || '').toUpperCase()}</p>
                      </a>
                      <ViewMore
                        viewMoreText={footerLocale.view_more_products}
                        viewLessText={footerLocale.view_less_products}
                        dataLength={page.products.length}
                      >
                        <ul className="typ-4">
                          {page.products.map(({ id, name, currentPath }) => (
                            <li key={id}>
                              <a href={`${prefixPath}${currentPath}`}>{name}</a>
                            </li>
                          ))}
                        </ul>
                      </ViewMore>
                    </>
                  ) : null}
                </>
              ) : (
                <InternalOrExternalPage page={page} pathLang={pathLang} />
              )}
            </div>
          ))}
        </div>

        <div className="typ-3 terms">
          <a href={`/${pathLang}${basesUrl.termsOfUse}`}>
            {footerLocale.terms_of_use.toUpperCase()}
          </a>
          <span
            className={classNames(shouldApplyContrast && 'image-with-contrast')}
          />
          <a href={`/${pathLang}${basesUrl.privacyPolicies}`}>
            {footerLocale.privacy_policy.toUpperCase()}
          </a>
        </div>
      </div>

      <div
        className={classNames(
          'planne-brand',
          shouldApplyContrast ? 'image-with-contrast' : 'image-without-contrast'
        )}
      >
        <p>{footerLocale.powered_by}</p>
        <a href="http://www.planne.com.br" target="_blank">
          <img src={icons.planneLogoIcon} alt={footerLocale.planne_logo} />
        </a>
        <p>
          © {footerLocale.all_rights_reserved} - {longName} {currentYear}
        </p>
      </div>
    </article>
  );
};

export default AlternativeFooter;
